import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Download, Twitter, Linkedin, Copy, Check, Info } from "lucide-react";
import { API } from "../../lib/constants";
import { trackEvent } from "../../lib/track";
import { toPng } from "html-to-image";

// Animated counter hook
const useCountUp = (end, duration = 1800, enabled = true) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!enabled || end === 0) return;
    let start = 0;
    const inc = end / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= end) {
        setVal(end);
        clearInterval(timer);
      } else {
        setVal(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, enabled]);
  return val;
};

export const GenderGapInfographic = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, insightsRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/discovery-insights`),
        ]);
        setData({ stats: statsRes.data, insights: insightsRes.data });
        setTimeout(() => setAnimate(true), 100);
      } catch (e) {
        void 0; // error handled silently
      }
    };
    fetchData();
  }, []);

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const url = await toPng(cardRef.current, { quality: 1, pixelRatio: 2, backgroundColor: "#0a0c14" });
      const a = document.createElement("a");
      a.download = "ai-gender-gap-infographic.png";
      a.href = url;
      a.click();
    } catch (e) {
      void 0; // error handled silently
    }
    setDownloading(false);
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.origin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareUrl = window.location.origin;

  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const { stats, insights } = data;
  const fd = stats.founder_distribution || {};
  const total = stats.total_apps;
  const knownFounders = insights.summary?.known_founders || 0;
  const discoveries = insights.discoveries || [];

  const femalePctNum = knownFounders > 0 ? (fd.female_only || 0) / knownFounders * 100 : 0;
  const genderCareGap = discoveries.find(d => d.title?.includes("Gender-Care"));
  const extractionProof = discoveries.find(d => d.title?.includes("Extraction"));
  const systemAware = discoveries.find(d => d.title?.includes("System-Aware"));

  const shareText = `Only ${femalePctNum.toFixed(1)}% of AI founders are women—yet they build ${genderCareGap?.stat || "19%"} of care-oriented AI. Explore ${total.toLocaleString()} apps:`;

  return (
    <div className="space-y-5">
      {/* The Infographic Card */}
      <div
        ref={cardRef}
        data-testid="infographic-card"
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: "linear-gradient(135deg, #0a0c14 0%, #0f1629 40%, #141833 100%)",
          padding: "32px",
          maxWidth: "680px",
          margin: "0 auto",
        }}
      >
        {/* Decorative glows */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(circle, #ec4899, transparent)" }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{ background: "radial-gradient(circle, #3b82f6, transparent)" }} />

        {/* Header */}
        <div className="relative z-10 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-widest text-pink-400/80">AI Observatory Research</span>
          </div>
          <h2 className="text-2xl font-black leading-tight" style={{ background: "linear-gradient(135deg, #f8fafc, #e2e8f0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            The AI Gender Gap
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Analyzing <AnimatedNum value={total} animate={animate} /> AI apps &middot;{" "}
            <AnimatedNum value={knownFounders} animate={animate} /> with known founders
          </p>
        </div>

        {/* Big Stat */}
        <div className="relative z-10 mb-5 p-4 rounded-xl" style={{ background: "rgba(236, 72, 153, 0.08)", border: "1px solid rgba(236, 72, 153, 0.2)" }}>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-4xl font-black text-pink-400">
              <AnimatedPct value={femalePctNum} animate={animate} />%
            </span>
            <span className="text-base text-slate-300">of AI founders are women</span>
          </div>
          <p className="text-slate-400 text-sm">
            Yet they build <span className="text-pink-300 font-semibold">{genderCareGap?.stat || "19.4%"}</span> of care-oriented AI
            — vs. just <span className="text-blue-300 font-semibold">3.5%</span> for male-only teams
          </p>
        </div>

        {/* Founder Breakdown Bars */}
        <div className="relative z-10 mb-5">
          <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Founder Composition</h3>
          <div className="space-y-3">
            <BarRow label="Male-Only" count={fd.male_only} total={knownFounders} color="#3b82f6" animate={animate} delay={200} />
            <BarRow label="Mixed Team" count={fd.mixed} total={knownFounders} color="#8b5cf6" animate={animate} delay={400} />
            <BarRow label="Female-Only" count={fd.female_only} total={knownFounders} color="#ec4899" animate={animate} delay={600} />
          </div>
        </div>

        {/* Discoveries */}
        <div className="relative z-10 grid grid-cols-2 gap-3 mb-4">
          <DiscoveryCard stat={extractionProof?.stat || "5/2,099"} label="female-founded apps are extraction-oriented" color="#10b981" />
          <DiscoveryCard stat={systemAware?.stat || "11.3%"} label="more system-aware AI from female founders" color="#8b5cf6" />
        </div>

        {/* Methodology note */}
        <div className="relative z-10 mb-3 px-3 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.03)" }}>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            <span className="text-slate-400 font-medium">Methodology:</span> Founder gender classified via multi-phase LLM analysis
            from public data, org records, and name-based inference. {Math.round((1 - (fd.unknown || 0) / total) * 100)}% resolved; {Math.round((fd.unknown || 0) / total * 100)}% remain unknown
            (primarily app store listings with no public founder data).
            Unknown apps are <span className="text-slate-400">excluded</span> from percentage calculations.
            n = {knownFounders.toLocaleString()} (known founder details).
          </p>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <span className="text-[10px] text-slate-400 font-medium">amplifyingconsciousinnovation.com</span>
          <span className="text-[10px] text-slate-400">&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
        </div>
      </div>

      {/* "What does unknown mean?" expandable */}
      <div className="max-w-[680px] mx-auto">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          data-testid="methodology-toggle"
          className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-300 transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
          {showMethodology ? "Hide methodology details" : "What does 'unknown' mean? How was this data collected?"}
        </button>
        {showMethodology && (
          <div data-testid="methodology-panel" className="mt-3 p-4 rounded-xl border border-border bg-card/50 text-sm text-slate-400 space-y-3 animate-fade-in">
            <div>
              <h4 className="text-slate-200 font-semibold mb-1">What "Unknown" means</h4>
              <p className="leading-relaxed">
                25.2% of apps (13,081) have <strong className="text-slate-300">unknown founder composition</strong>. These are primarily:
              </p>
              <ul className="list-disc list-inside mt-1 space-y-0.5 text-slate-400">
                <li>HuggingFace community projects by anonymous/pseudonymous users</li>
                <li>Open-source models without identifiable individual creators</li>
                <li>Small-scale apps with no publicly available leadership information</li>
              </ul>
              <p className="mt-2">Unknown apps are <strong className="text-slate-300">excluded from all percentage calculations</strong> to avoid skewing results.</p>
            </div>
            <div>
              <h4 className="text-slate-200 font-semibold mb-1">Data collection methodology</h4>
              <p className="leading-relaxed">
                Founder data was resolved through a 3-phase enrichment pipeline:
              </p>
              <ol className="list-decimal list-inside mt-1 space-y-0.5 text-slate-400">
                <li><strong className="text-slate-400">HuggingFace org mapping</strong> — matched 7,032 apps to known organizations (Google, Meta, Microsoft, etc.)</li>
                <li><strong className="text-slate-400">LLM batch classification</strong> — GPT-4.1-mini analyzed product names, taglines, URLs across 2,000+ batches</li>
                <li><strong className="text-slate-400">Web search + LLM</strong> — DuckDuckGo search for founder info on high-value apps</li>
              </ol>
            </div>
            <div>
              <h4 className="text-slate-200 font-semibold mb-1">Country data</h4>
              <p className="leading-relaxed">
                62.1% of apps have identified countries. Variants have been normalized (e.g., "United States" → "USA", "United Kingdom" → "UK").
                37.9% remain unknown — primarily HuggingFace community projects and tools without geographic attribution.
              </p>
            </div>
            <div>
              <h4 className="text-slate-200 font-semibold mb-1">Confidence & limitations</h4>
              <p className="leading-relaxed">
                LLM-inferred classifications carry inherent uncertainty. The model may default to "male_only" for ambiguous cases 
                given the industry's demographic skew. All statistical findings use Wilson score confidence intervals at 95%.
                This dataset is intended for research exploration, not definitive demographic claims.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons — Download first, then share */}
      <div className="space-y-3" data-testid="infographic-actions">
        <div className="flex items-center justify-center gap-3">
          <button onClick={handleDownload} disabled={downloading} data-testid="infographic-download-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
            <Download className="w-4 h-4" />
            {downloading ? "Saving..." : "1. Download Image"}
          </button>
          <button onClick={handleCopyLink} data-testid="infographic-copy-link"
            className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg hover:bg-muted transition-colors text-sm">
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy Text"}
          </button>
        </div>
        <div className="flex items-center justify-center gap-3">
          <p className="text-xs text-muted-foreground">2. Share with your image:</p>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noopener noreferrer" data-testid="infographic-share-linkedin"
            onClick={() => trackEvent("share_linkedin", "infographic")}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm">
            <Linkedin className="w-4 h-4" />LinkedIn
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noopener noreferrer" data-testid="infographic-share-twitter"
            onClick={() => trackEvent("share_twitter", "infographic")}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm">
            <Twitter className="w-4 h-4" />X / Twitter
          </a>
        </div>
        <p className="text-[10px] text-muted-foreground/60 text-center">Tip: Download the image first, then attach it when posting on LinkedIn or X for a rich visual share</p>
      </div>
    </div>
  );
};

// Animated number display
const AnimatedNum = ({ value, animate }) => {
  const v = useCountUp(value, 1600, animate);
  return <span className="tabular-nums">{Math.round(v).toLocaleString()}</span>;
};

const AnimatedPct = ({ value, animate }) => {
  const v = useCountUp(value, 1800, animate);
  return <span className="tabular-nums">{v.toFixed(1)}</span>;
};

// Bar row with animated width
const BarRow = ({ label, count, total, color, animate, delay = 0 }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!animate) return;
    const t = setTimeout(() => setWidth(Math.max(pct, 2)), delay);
    return () => clearTimeout(t);
  }, [animate, pct, delay]);

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-400 w-24 text-right">{label}</span>
      <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
        <div
          className="h-full rounded-full flex items-center justify-end pr-2"
          style={{
            width: `${width}%`,
            background: color,
            minWidth: width > 0 ? "40px" : "0px",
            transition: "width 1.2s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {width > 0 && (
            <span className="text-xs font-bold text-white/90">{pct.toFixed(1)}%</span>
          )}
        </div>
      </div>
      <span className="text-xs text-slate-400 w-16 tabular-nums">{count?.toLocaleString()}</span>
    </div>
  );
};

const DiscoveryCard = ({ stat, label, color }) => (
  <div className="p-4 rounded-xl" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
    <span className="text-2xl font-black" style={{ color }}>{stat}</span>
    <p className="text-xs text-slate-400 mt-1 leading-snug">{label}</p>
  </div>
);
